/** Cálculos inventario de gestión — líneas NF. */

import { ocDisplayLabel } from './inventarioOcUtils';
import { ESTADO_DISPONIBLE } from './inventarioEstados';

/** Parsea números con formato CL/BR/US o valor ya numérico. */
export function parseNum(v) {
  if (v == null || v === '') return null;
  if (typeof v === 'number' && Number.isFinite(v)) return v;

  const s = String(v).trim();
  if (!s) return null;

  const hasComma = s.includes(',');
  const hasDot = s.includes('.');

  let normalized;
  if (hasComma && hasDot) {
    // 1.234,56 (CL/BR) vs 1,234.56 (US)
    normalized =
      s.lastIndexOf(',') > s.lastIndexOf('.')
        ? s.replace(/\./g, '').replace(',', '.')
        : s.replace(/,/g, '');
  } else if (hasComma) {
    normalized = s.replace(',', '.');
  } else if (hasDot) {
    const parts = s.split('.');
    // Un solo punto con 1-3 decimales → separador decimal (848.71, 145.00)
    if (parts.length === 2 && parts[1].length > 0 && parts[1].length <= 3) {
      normalized = s;
    } else {
      normalized = s.replace(/\./g, '');
    }
  } else {
    normalized = s;
  }

  const n = Number(normalized);
  return Number.isFinite(n) ? n : null;
}

export function roundN(v, dec = 3) {
  if (v == null || Number.isNaN(v)) return null;
  const f = 10 ** dec;
  return Math.round(Number(v) * f) / f;
}

export function calcTotalPcs(paquetes, pcsPaq, grado) {
  const p = parseNum(paquetes);
  if (isGradoRw(grado)) {
    if (p == null) return null;
    return Math.round(p);
  }
  const pp = parseNum(pcsPaq);
  if (p == null || pp == null) return null;
  return Math.round(p * pp);
}

export function isGradoRw(grado) {
  return String(grado || '').toUpperCase().includes('RW');
}

/** Dimensión RW en metros (grilla suele traer mm: 1220 → 1,220). */
function dimRwMetros(v) {
  const n = parseNum(v);
  if (n == null) return null;
  if (n >= 100) return n / 1000;
  return n;
}

export function calcTotalM3(linea) {
  const grado = linea.grado;
  const esp = parseNum(linea.espesor);
  const anc = parseNum(linea.ancho);
  const lar = parseNum(linea.largo);

  if (isGradoRw(grado)) {
    const anchoM = dimRwMetros(linea.ancho);
    const largoM = dimRwMetros(linea.largo);
    const cast = parseNum(linea.castigo_rw);
    const paq = parseNum(linea.paquetes);
    const alt = parseNum(linea.altura_rw);
    if (anchoM == null || largoM == null || cast == null || paq == null || alt == null) {
      return null;
    }
    // RW: 1 × ancho(m) × largo(m) × castigo × paquetes × altura RW
    return roundN(1 * anchoM * largoM * cast * paq * alt, 3);
  }

  if (esp == null || lar == null) return null;
  if (anc == null) return null;
  const totalPcs = parseNum(linea.total_pcs);
  if (totalPcs == null) return null;
  const base = (esp * anc * lar) / 1_000_000_000;
  return roundN(base * totalPcs, 3);
}

/** BRL/u M³ solo existe cuando la NF aporta un precio para la línea. */
export function resolveBrlUnitM3(linea) {
  const fromNf = parseNum(linea.precio_brl_unit_m3);
  return fromNf == null ? null : roundN(fromNf, 2);
}

export function calcTotalBrl(linea) {
  const m3 = parseNum(linea.total_m3);
  const brl = parseNum(linea.precio_brl_unit_m3);
  if (m3 == null || brl == null) return null;
  return roundN(m3 * brl, 2);
}

/** Excel: TC = REDONDEAR(BRL/u M³ / USD/u M³; 3). */
export function calcTcCalculado(linea) {
  const brl = parseNum(linea.precio_brl_unit_m3);
  const usd = parseNum(linea.precio_usd_unit_m3);
  if (brl == null || usd == null || usd === 0) return null;
  return roundN(brl / usd, 3);
}

/** Diferencia = TC calculado − TC BCB. */
export function calcDiferencia(linea, tcBcb) {
  const tc = calcTcCalculado(linea);
  const bcb = parseNum(tcBcb);
  if (tc == null || bcb == null) return null;
  return roundN(tc - bcb, 4);
}

export function calcTotalUsd(linea) {
  const m3 = parseNum(linea.total_m3);
  const usd = parseNum(linea.precio_usd_unit_m3);
  if (m3 == null || usd == null) return null;
  return roundN(m3 * usd, 2);
}

/** Campos que al cambiar deben recalcular Total M³ (y Total BRL salvo override manual). */
export const M3_DRIVER_FIELDS = new Set([
  'paquetes',
  'pcs_paq',
  'altura_rw',
  'castigo_rw',
  'grado',
  'espesor',
  'ancho',
  'largo',
]);

/** Aplica patch de línea y flags de override para totales calculados editables. */
export function mergeLineaPatch(ln, patch) {
  const next = { ...ln, ...patch };
  if (patch.total_m3 !== undefined) {
    next._total_m3_manual = true;
  }
  if (patch.total_brl_m3 !== undefined) {
    next._total_brl_m3_manual = true;
  }
  if ([...M3_DRIVER_FIELDS].some((f) => patch[f] !== undefined)) {
    next._total_m3_manual = false;
    next._total_brl_m3_manual = false;
  }
  if (patch.precio_brl_unit_m3 !== undefined) {
    next._total_brl_m3_manual = false;
  }
  return next;
}

export function recalcLinea(linea, tcBcb) {
  const total_pcs = calcTotalPcs(linea.paquetes, linea.pcs_paq, linea.grado);
  const computed_m3 = calcTotalM3({ ...linea, total_pcs });
  let total_m3;
  if (linea._total_m3_manual) {
    total_m3 = linea.total_m3;
  } else if (computed_m3 != null) {
    total_m3 = computed_m3;
  } else {
    total_m3 = linea.total_m3 ?? null;
  }

  const precio_brl_unit_m3 = resolveBrlUnitM3(linea);
  const calc = { ...linea, total_m3, precio_brl_unit_m3, total_pcs };
  const computed_brl = calcTotalBrl(calc);
  let total_brl_m3;
  if (linea._total_brl_m3_manual) {
    total_brl_m3 = linea.total_brl_m3;
  } else if (computed_brl != null) {
    total_brl_m3 = computed_brl;
  } else {
    total_brl_m3 = linea.total_brl_m3 ?? null;
  }

  const calc2 = { ...calc, total_brl_m3 };
  const tc_calculado = calcTcCalculado(calc2);
  const diferencia = calcDiferencia(calc2, tcBcb);
  const total_usd_m3 = calcTotalUsd(calc2);
  return {
    ...linea,
    total_pcs,
    total_m3,
    precio_brl_unit_m3,
    total_brl_m3,
    tc_calculado,
    diferencia,
    total_usd_m3,
  };
}

export function recalcNota(nota) {
  const lineas = (nota.lineas || []).map((ln) => recalcLinea(ln, nota.tc_bcb));
  return { ...nota, lineas };
}

export function fmtNum(v, dec = 3) {
  if (v == null || v === '') return '';
  const n = Number(v);
  if (Number.isNaN(n)) return '';
  return new Intl.NumberFormat('es-CL', {
    minimumFractionDigits: 0,
    maximumFractionDigits: dec,
  }).format(n);
}

export function tipoNfLabel(tipo) {
  const t = String(tipo ?? '').trim();
  if (t === '0') return 'Entrada';
  if (t === '1') return 'Salida';
  return t || '—';
}

/** CNPJ/RUT: solo dígitos (vista previa y edición). */
export function cleanCnpj(v) {
  if (v == null || v === '') return '';
  return String(v).replace(/\D/g, '');
}

/** Enteros: solo dígitos. */
export function sanitizeIntInput(v) {
  if (v == null || v === '') return '';
  return String(v).replace(/\D/g, '');
}

/** Decimales: dígitos y un separador (. o ,). */
export function sanitizeDecimalInput(v) {
  if (v == null || v === '') return '';
  let s = String(v).replace(/[^\d.,]/g, '');
  const sep = s.search(/[.,]/);
  if (sep < 0) return s;
  return s.slice(0, sep + 1) + s.slice(sep + 1).replace(/[.,]/g, '');
}

export function newEmptyLinea(ocDefaults = {}) {
  return recalcLinea({
    paquetes: '',
    pcs_paq: '',
    total_pcs: null,
    altura_rw: '',
    castigo_rw: ocDefaults.castigo_rw_default ?? '',
    grado: ocDefaults.calidades?.[0] || '',
    espesor: '',
    ancho: '',
    largo: '',
    total_m3: null,
    precio_brl_unit_m3: '',
    total_brl_m3: null,
    tc_calculado: null,
    diferencia: null,
    precio_usd_unit_m3: '',
    total_usd_m3: null,
  });
}

export function newManualNota({ proveedor, producto, oc, ocExtras = {} }) {
  const nota = recalcNota({
    _key: `manual-${Date.now()}`,
    tipo_nf: '0',
    cnpj: cleanCnpj(proveedor?.tax || ''),
    id_proveedor: proveedor?.id_proveedor,
    nombre_proveedor: proveedor?.nombre || '',
    cnpj_match: true,
    id_ordencompra: oc?.id_ordencompra,
    oc_label: ocDisplayLabel(oc),
    id_producto: producto?.id_producto,
    producto_nombre: producto?.nombre_producto || '',
    almacen: '',
    booking: '',
    nro_nf: '',
    incoterm_id: oc?.incoterm,
    incoterm_label: '',
    categoria: '',
    especie: '',
    fecha_nf: '',
    valor_total_nf: '',
    valor_total_prod: '',
    peso_liquido: '',
    peso_bruto: '',
    tc_bcb: '',
    llave: '',
    archivo_origen: 'manual',
    parse_warning: null,
    calidades_oc: [],
    status_oc: ocExtras.status_oc || '',
    etapa_entrega: '',
    quien_retira: '',
    fecha_mov_almacen: '',
    tipo_movimiento: '',
    fecha_ingreso_puerto: '',
    estado_mercaderia: ESTADO_DISPONIBLE,
    observacion: '',
    pago: '',
    descuentos: '',
    eta_puerto_almac: '',
    comentarios: '',
    lineas: [newEmptyLinea({ castigo_rw_default: oc?.allowance, calidades: ocExtras.calidades })],
  });
  return nota;
}

function nuevoAcumuladoGrado(grado) {
  return {
    grado,
    paquetes: 0,
    total_pcs: 0,
    total_m3: 0,
    total_brl_m3: 0,
    total_usd_m3: 0,
  };
}

function acumularLineaEnTotal(acc, ln) {
  acc.paquetes += parseNum(ln.paquetes) || 0;
  acc.total_pcs += parseNum(ln.total_pcs) || 0;
  acc.total_m3 += parseNum(ln.total_m3) || 0;
  acc.total_brl_m3 += parseNum(ln.total_brl_m3) || 0;
  acc.total_usd_m3 += parseNum(ln.total_usd_m3) || 0;
}

export function gradoLineaInventario(ln) {
  return String(ln?.grado || '').trim() || 'Sin grado';
}

function gradoLinea(ln) {
  return gradoLineaInventario(ln);
}

/**
 * Agrupa líneas por grado (orden calidades OC, luego aparición).
 * @param {number|null} indiceLineaAlFinal — índice en `lineas` que debe quedar al final de su grado (p. ej. línea recién editada).
 */
export function ordenarLineasPorGrado(lineas, calidadesOc = [], indiceLineaAlFinal = null) {
  const calidades = (calidadesOc || []).map((g) => String(g || '').trim()).filter(Boolean);
  const byGrado = new Map();
  const seen = [];
  for (const [idx, ln] of (lineas || []).entries()) {
    const g = gradoLinea(ln);
    if (!byGrado.has(g)) {
      byGrado.set(g, []);
      seen.push(g);
    }
    byGrado.get(g).push({ ln, idx });
  }
  const rank = (g) => {
    const i = calidades.indexOf(g);
    if (i >= 0) return i;
    return calidades.length + seen.indexOf(g);
  };
  const order = [...seen].sort((a, b) => {
    const dr = rank(a) - rank(b);
    if (dr !== 0) return dr;
    return a.localeCompare(b, 'es');
  });
  const ultimoIdx = Number.isInteger(indiceLineaAlFinal) ? indiceLineaAlFinal : null;
  return order.flatMap((g) => {
    const items = byGrado.get(g);
    if (ultimoIdx == null) return items.map((item) => item.ln);
    const alFinal = items.filter((item) => item.idx === ultimoIdx);
    const resto = items.filter((item) => item.idx !== ultimoIdx);
    return [...resto, ...alFinal].map((item) => item.ln);
  });
}

/**
 * Bloques contiguos del mismo grado (mismo orden que en la NF) con subtotal al cierre de cada bloque.
 */
export function bloquesLineasConTotalesPorGrado(lineas) {
  const bloques = [];
  let actual = null;
  for (const [idx, ln] of (lineas || []).entries()) {
    const grado = gradoLinea(ln);
    if (!actual || actual.grado !== grado) {
      if (actual) bloques.push(actual);
      actual = { grado, items: [], tot: nuevoAcumuladoGrado(grado) };
    }
    actual.items.push({ ln, idx });
    acumularLineaEnTotal(actual.tot, ln);
  }
  if (actual) bloques.push(actual);
  return bloques;
}

/** Totales de composición agrupados por grado (modal Editar NF). */
export function totalesPorGradoDesdeLineas(lineas) {
  return bloquesLineasConTotalesPorGrado(lineas).map((b) => b.tot);
}
